'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import Link, { useLinkStatus } from 'next/link';
import { useRouter } from 'next/navigation';
import { formatReference, looksLikeReference, parseReference, referenceToSlug } from '@/lib/bibleReference';
import { APP_STORE_URL } from '@/lib/appDeepLink';
import { extractReferenceFromText } from '@/lib/suggestionResolver';
import { RichText } from '@/components/Markdown';
import { queryToAskSlug } from '@/lib/askAnswers';
import { trackSearch, trackSearchResultClick } from '@/lib/analytics';

/**
 * Per-tap "this is loading" feedback for a Link, independent of the
 * destination route's own rendering. Verse pages are `force-dynamic` (see
 * [slug]/page.tsx) specifically so their Suspense-streamed insights section
 * works correctly — but that same dynamic-rendering requirement makes a
 * route-level loading.tsx incompatible with correct 404 status codes
 * (confirmed empirically 2026-07-09: loading.tsx forces Next to commit a 200
 * response before notFound() can run). useLinkStatus sidesteps that
 * entirely — it's a pure client-side transition signal, so this still
 * satisfies "navigation feedback is instant" without touching the server
 * response at all.
 */
function NavSpinner() {
  const { pending } = useLinkStatus();
  if (!pending) return null;
  return (
    <span
      className="inline-block w-3 h-3 ml-2 align-middle rounded-full border-2 border-current border-t-transparent animate-spin"
      aria-hidden="true"
    />
  );
}

/**
 * Chat-style send button — hidden/disabled on an empty input, animates in
 * (opacity + scale, ~150ms) the moment the user types something, and swaps
 * its arrow for a spinner while a submission is resolving. Vellum-on-ink:
 * bg-foreground (the warm cream token) with an ink (bg-background-colored)
 * arrow, matching the same high-contrast-circle convention already used by
 * the "Ask"/"Get the app" pills elsewhere on this page — reads as the most
 * chat-like affordance against the gold-accent-sparingly v6 palette (gold is
 * reserved for rubrics/rules, never a button fill).
 */
function SendButton({ visible, pending }: { visible: boolean; pending: boolean }) {
  return (
    <button
      type="submit"
      aria-label="Search"
      disabled={!visible || pending}
      tabIndex={visible ? 0 : -1}
      className={`absolute right-2 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-full bg-foreground text-background transition-all duration-150 ease-out disabled:cursor-not-allowed focus:outline-none focus-visible:ring-2 focus-visible:ring-[rgba(212,163,62,0.7)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0d0b08] ${
        visible ? 'opacity-100 scale-100' : 'opacity-0 scale-75 pointer-events-none'
      }`}
    >
      {pending ? (
        <span
          className="h-4 w-4 rounded-full border-2 border-current border-t-transparent animate-spin"
          aria-hidden="true"
        />
      ) : (
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 19V5M5 12l7-7 7 7" />
        </svg>
      )}
    </button>
  );
}

interface DirectMatch {
  reference: string;
  slug: string;
}

interface Suggestion {
  text: string;
  href: string;
}

interface SearchResponse {
  directMatch: DirectMatch | null;
  suggestions: Suggestion[];
}

// 400ms, up from 250 (Jay report 2026-09: search fires while people are
// still typing). Only the /api/verse-search autocomplete fetch is gated by
// this — navigation itself only ever happens from handleSubmit/resolveAndGo
// on explicit Enter/tap, never from this debounced effect.
const DEBOUNCE_MS = 400;

/**
 * Autocomplete-as-you-type, proxied through /api/verse-search (server-side —
 * the Sola API's CORS is closed to this domain, so the browser can never
 * call it directly). Accepts reference shorthand: if the typed text parses
 * as a real reference, a "Go to {canonical ref}" result appears INSTANTLY,
 * client-side, with no network round trip at all (fix 2026-07-09 — see
 * VERSE_EXPLAINED_PLAN.md §2, "make first paint instant").
 *
 * Every suggestion is a real navigable link (verse page, topic page, or a
 * pre-filled/auto-run search) resolved server-side — fixes the "tapping a
 * suggestion just shows more suggestions" dead loop (2026-07-09 bug report).
 * The worst-case fallback target (no verse or topic match) lands back here
 * with `?q=` set — read client-side from window.location rather than a
 * server searchParams prop so this page can stay statically prerendered.
 */
export default function VerseSearchClient() {
  const router = useRouter();
  const [query, setQuery] = useState('');
  const [apiResult, setApiResult] = useState<SearchResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Set when a submitted query has no destination page: the panel becomes
  // a chat-style ANSWER surface — a streamed scholarly answer (proxied
  // /api/search/description SSE) plus semantically-matched passages. This
  // is what makes send always land on something real (Jay 2026-07-11:
  // relabeling the suggestion list was not an answer).
  const [answerFor, setAnswerFor] = useState<string | null>(null);
  const [answerText, setAnswerText] = useState('');
  const [answerStreaming, setAnswerStreaming] = useState(false);
  const [answerError, setAnswerError] = useState(false);
  const [passages, setPassages] = useState<
    { reference: string; text: string; relevance: string; href: string | null }[]
  >([]);
  const answerAbortRef = useRef<AbortController | null>(null);
  const [shareCopied, setShareCopied] = useState(false);
  const [error, setError] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoRanRef = useRef(false);

  // Local, instant parse — the reference parser is pure/client-safe (no I/O)
  // so this runs synchronously on every keystroke with zero network cost.
  const localMatch = useMemo(() => {
    const trimmed = query.trim();
    if (!looksLikeReference(trimmed)) return null;
    const parsed = parseReference(trimmed);
    if (!parsed.ok) return null;
    return { reference: formatReference(parsed.reference), slug: referenceToSlug(parsed.reference) };
  }, [query]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = query.trim();

    // A locally-resolved reference is shown instantly (see localMatch above)
    // and needs no suggestions list underneath it — skip the API entirely.
    if (localMatch) {
      setApiResult(null);
      setLoading(false);
      return;
    }

    if (trimmed.length < 2) {
      setApiResult(null);
      return;
    }
    debounceRef.current = setTimeout(() => {
      void runSearch(trimmed);
    }, DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, Boolean(localMatch)]);

  // If the page loaded with a prefilled "?q=" (the "never a dead loop"
  // fallback target for suggestions with no better verse/topic landing page
  // — see suggestionResolver.ts), pre-fill the box; the debounce effect
  // above picks it up from there (instant local match, or one debounced
  // fetch) exactly as if the user had just typed it.
  useEffect(() => {
    if (autoRanRef.current) return;
    autoRanRef.current = true;
    const q = new URLSearchParams(window.location.search).get('q')?.trim();
    if (q && q.length >= 2) {
      setQuery(q);
      // Deliver the full submit experience, not just a prefilled input —
      // this is the landing path for pre-hydration native form submits.
      void resolveAndGo(q);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** A suggestion href is a real DESTINATION if it leads somewhere new
      (a verse page or a /verses-about topic). Hrefs that point back at
      this page with ?q= are query REFINEMENTS, not destinations —
      navigating to them looks like nothing happened. */
  function isDestination(href: string) {
    return !href.startsWith('/bible-verse-explained?q=');
  }

  async function runSearch(q: string) {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`/api/verse-search?q=${encodeURIComponent(q)}`);
      if (!res.ok) throw new Error('search failed');
      const json: SearchResponse = await res.json();
      setApiResult(json);
      return json;
    } catch {
      setError('Search is temporarily unavailable. Try a reference like "John 3:16".');
      return null;
    } finally {
      setLoading(false);
    }
  }

  /**
   * Chat-style submit: Enter (native form submit) and the send-button click
   * both land here. Picks the solidest available resolution in priority
   * order, cheapest/most-certain first, so the common case (a reference,
   * typed or embedded in a sentence) navigates instantly with zero network
   * round trips — the same "chat message -> immediate transition" feel as
   * tapping a suggestion:
   *
   *   1. Whole input parses as a reference (localMatch, e.g. "John 3:16").
   *   2. A reference is embedded in free text (e.g. "what does romans 8:28
   *      mean") — extractReferenceFromText is the exact same pure/no-I/O
   *      parse suggestionResolver.ts already uses server-side for
   *      suggestion hrefs, so this is just as instant as case 1.
   *   3. The debounced autocomplete fetch already resolved a directMatch or
   *      suggestions for this query — reuse it, no extra round trip.
   *   4. Nothing resolved yet (user hit Enter faster than the 250ms
   *      debounce, or the upstream API is slow) — show the button's pending
   *      spinner, run one un-debounced fetch, then navigate to whatever it
   *      returns. Never a dead end: falls back to this same page with `?q=`
   *      set, matching resolveSuggestionHref's fallback contract.
   */
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = query.trim();
    if (!trimmed || submitting) return;

    if (localMatch) {
      trackSearch(trimmed, 'reference_local');
      router.push(`/bible-verse-explained/${localMatch.slug}`);
      return;
    }

    // Fast paths off already-fetched autocomplete state:
    if (!extractReferenceFromText(trimmed)) {
      if (apiResult?.directMatch) {
        trackSearch(trimmed, 'reference_api', apiResult.suggestions?.length ?? 0);
        router.push(`/bible-verse-explained/${apiResult.directMatch.slug}`);
        return;
      }
      const preResolvedDest = apiResult?.suggestions?.find((sug) => isDestination(sug.href));
      if (preResolvedDest) {
        trackSearch(trimmed, 'suggestion_auto', apiResult?.suggestions?.length ?? 0);
        router.push(preResolvedDest.href);
        return;
      }
      if (apiResult?.suggestions && apiResult.suggestions.length > 0) {
        trackSearch(trimmed, 'answer', apiResult.suggestions.length);
        void startAnswer(trimmed);
        return;
      }
    }

    // Delegates rather than duplicating, so a submitted query is tracked
    // exactly once — every branch above has already returned.
    await resolveAndGo(trimmed);
  }

  /**
   * State-independent resolution for a submitted query — used by
   * handleSubmit's fallback AND by the ?q= mount path (which is how a
   * pre-hydration native form submit degrades: before React attaches
   * onSubmit, Enter does a real GET to ?q=..., and this picks it up and
   * delivers the same experience — found live 2026-07-11 when a cold
   * deployment let Enter through before hydration).
   */
  async function resolveAndGo(trimmed: string) {
    const embedded = extractReferenceFromText(trimmed);
    if (embedded) {
      trackSearch(trimmed, 'reference_embedded');
      router.push(`/bible-verse-explained/${referenceToSlug(embedded)}`);
      return;
    }

    setSubmitting(true);
    try {
      const json = await runSearch(trimmed);
      const count = json?.suggestions?.length ?? 0;
      if (json?.directMatch) {
        trackSearch(trimmed, 'reference_api', count);
        router.push(`/bible-verse-explained/${json.directMatch.slug}`);
        return;
      }
      const dest = json?.suggestions?.find((sug) => isDestination(sug.href));
      if (dest) {
        trackSearch(trimmed, 'suggestion_auto', count);
        router.push(dest.href);
        return;
      }
      // json === null means the fetch failed; we still fall through to an
      // answer (the "never a dead end" contract) but record it as an error
      // so a broken upstream can't masquerade as a content gap in the data.
      trackSearch(trimmed, json ? 'answer' : 'error', count);
      void startAnswer(trimmed);
    } finally {
      setSubmitting(false);
    }
  }

  /** Copy the canonical share link for the current answer and warm its
      cache with a background fetch, so the first Reddit clicker doesn't
      pay the generation wait. */
  async function shareAnswer() {
    if (!answerFor) return;
    const slug = queryToAskSlug(answerFor);
    if (!slug) return;
    const url = `${window.location.origin}/bible-verse-explained/ask/${slug}`;
    try {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2200);
    } catch {
      window.prompt('Copy this link:', url);
    }
    // fire-and-forget cache warm; errors are irrelevant here
    void fetch(url).catch(() => {});
  }

  /**
   * The chat payoff for topical questions: stream the scholarly answer
   * (SSE chunks) into the panel and fetch semantically-matched passages in
   * parallel. Both go through same-origin proxies (upstream CORS is closed).
   * A new submit or any typing aborts the in-flight stream.
   */
  async function startAnswer(q: string) {
    answerAbortRef.current?.abort();
    const controller = new AbortController();
    answerAbortRef.current = controller;

    setAnswerFor(q);
    setAnswerText('');
    setPassages([]);
    setAnswerError(false);
    setAnswerStreaming(true);

    const passagesPromise = fetch('/api/verse-search/passages', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query: q }),
      signal: controller.signal,
    })
      .then((r) => (r.ok ? r.json() : { passages: [] }))
      .then((j) => {
        if (!controller.signal.aborted) setPassages(j.passages ?? []);
      })
      .catch(() => {});

    try {
      const res = await fetch('/api/verse-search/answer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: q }),
        signal: controller.signal,
      });
      if (!res.ok || !res.body) throw new Error('answer failed');

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let accumulated = '';
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        for (const line of lines) {
          const m = line.match(/^data:\s*(.+)$/);
          if (!m) continue;
          try {
            const payload = JSON.parse(m[1]);
            if (typeof payload.chunk === 'string') {
              accumulated += payload.chunk;
              setAnswerText(accumulated);
            }
          } catch {
            /* non-JSON keepalive lines are fine to skip */
          }
        }
      }
      // Don't let a still-empty answer look like success.
      if (!controller.signal.aborted && accumulated.trim().length === 0) {
        setAnswerError(true);
      }
    } catch {
      if (!controller.signal.aborted) setAnswerError(true);
    } finally {
      if (!controller.signal.aborted) setAnswerStreaming(false);
    }
    await passagesPromise;
  }

  const directMatch = localMatch ?? apiResult?.directMatch ?? null;
  const suggestions = localMatch ? [] : (apiResult?.suggestions ?? []);
  const hasResults = Boolean(directMatch || suggestions.length > 0);

  return (
    <div className="w-full max-w-xl mx-auto">
      {/* action/method/name make a PRE-HYDRATION Enter degrade to a real
          GET ?q=... instead of a silent same-URL reload; the mount effect
          above picks that up and completes the flow. */}
      <form onSubmit={handleSubmit} action="/bible-verse-explained" method="get" className="relative">
        <input
          type="text"
          name="q"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            answerAbortRef.current?.abort();
            setAnswerFor(null);
            setAnswerStreaming(false);
          }}
          placeholder='Try "anxiety" or "John 3:16"...'
          disabled={submitting}
          className="w-full h-14 rounded-full border border-border-strong bg-card pl-6 pr-16 text-base placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-[rgba(212,163,62,0.45)] focus:border-[rgba(212,163,62,0.5)] transition-colors disabled:opacity-70"
        />
        <SendButton visible={query.trim().length > 0} pending={submitting} />
      </form>

      {/* Debounced-autocomplete-in-flight feedback (distinct from `submitting`,
          which only covers the brief post-submit resolve in handleSubmit's
          fallback path) — shown only while nothing is renderable yet, so it
          never fights with the results panel below. */}
      {loading && !hasResults && !error && (
        <p className="text-xs text-muted-foreground mt-3 px-1" aria-live="polite">
          Searching…
        </p>
      )}

      {error && <p className="text-sm text-red-400 mt-3">{error}</p>}

      {/* ————— The answer surface (topical questions) —————
          The chat payoff: the question echoes as a sent bubble, then the
          answer streams in below with the matching passages. */}
      {answerFor && (
        <div className="mt-4">
          <div className="flex justify-end mb-4">
            <div className="rounded-2xl rounded-br-[4px] bg-[rgba(242,234,216,0.94)] px-4 py-3 max-w-[24rem] shadow-[0_14px_30px_-14px_rgba(0,0,0,0.7)]">
              <p className="text-[#2b2418] text-sm leading-snug">{answerFor}</p>
            </div>
          </div>

          <div className="bg-card border border-border rounded-2xl p-5 sm:p-6 text-left">
            <div className="mb-3 flex items-center justify-between gap-4">
              <p className="text-xs font-medium uppercase tracking-widest text-accent">
                {answerStreaming ? 'Sola · answering' : 'Sola'}
              </p>
              {!answerStreaming && answerText && !answerError && (
                <button
                  type="button"
                  onClick={() => void shareAnswer()}
                  className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus-visible:text-accent"
                  aria-live="polite"
                >
                  {shareCopied ? (
                    <>
                      <svg className="h-3.5 w-3.5 text-accent" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Link copied
                    </>
                  ) : (
                    <>
                      <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
                      </svg>
                      Share this answer
                    </>
                  )}
                </button>
              )}
            </div>

            {answerError ? (
              <p className="text-sm text-muted-foreground">
                Couldn&rsquo;t load an answer just now. Try again in a moment, or{' '}
                <a
                  href={APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-foreground border-b border-accent pb-0.5 hover:text-accent transition-colors"
                >
                  ask it in the app
                </a>
                .
              </p>
            ) : answerText ? (
              <RichText text={answerText} className="text-sm leading-relaxed text-muted-foreground [&_h1]:text-base [&_h2]:text-base [&_h3]:text-sm [&_h3]:font-sans [&_h3]:font-semibold [&_h1]:font-sans [&_h2]:font-sans [&_h1]:font-semibold [&_h2]:font-semibold" />
            ) : (
              <div className="space-y-2.5" aria-hidden="true">
                <div className="skeleton-bar h-3.5 w-full" />
                <div className="skeleton-bar h-3.5 w-[92%]" />
                <div className="skeleton-bar h-3.5 w-[74%]" />
              </div>
            )}
            {answerStreaming && answerText && (
              <span
                className="ml-1 inline-block h-3.5 w-[2px] translate-y-0.5 bg-[rgba(212,163,62,0.9)] animate-pulse"
                aria-hidden="true"
              />
            )}

            {passages.length > 0 && (
              <div className="mt-6 pt-5 border-t border-border">
                <p className="text-xs font-medium uppercase tracking-widest text-muted-foreground mb-3">
                  Key passages
                </p>
                <ul className="space-y-3">
                  {passages.map((pv) => (
                    <li key={pv.reference}>
                      {pv.href ? (
                        <Link href={pv.href} prefetch={false} className="group block">
                          <span className="font-mono text-xs tracking-[0.08em] text-accent">
                            {pv.reference}
                            <NavSpinner />
                          </span>
                          <span className="block font-scripture text-[1.02rem] leading-snug text-foreground mt-0.5 group-hover:text-accent transition-colors">
                            &ldquo;{pv.text}&rdquo;
                          </span>
                          <span className="block text-xs text-muted-foreground mt-1">{pv.relevance}</span>
                        </Link>
                      ) : (
                        <div>
                          <span className="font-mono text-xs tracking-[0.08em] text-accent">{pv.reference}</span>
                          <span className="block font-scripture text-[1.02rem] leading-snug text-foreground mt-0.5">
                            &ldquo;{pv.text}&rdquo;
                          </span>
                          <span className="block text-xs text-muted-foreground mt-1">{pv.relevance}</span>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {!answerError && (
              <div className="mt-6 pt-5 border-t border-border">
                <p className="text-sm text-muted-foreground">
                  Keep the conversation going.{' '}
                  <a
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-foreground border-b border-accent pb-0.5 hover:text-accent transition-colors"
                  >
                    Ask follow-ups in Sola
                  </a>
                  . Every answer cites the text.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {!answerFor && hasResults && (
        <div className="bg-card border border-border rounded-2xl p-5 mt-4">
          {directMatch && (
            <Link
              href={`/bible-verse-explained/${directMatch.slug}`}
              prefetch={true}
              onClick={() =>
                trackSearchResultClick(query, 'direct_match', directMatch.slug)
              }
              className="block mb-3 pb-3 border-b border-border group last:mb-0 last:pb-0 last:border-b-0"
            >
              <span className="text-xs font-medium uppercase tracking-widest text-accent">
                Go to
              </span>
              <span className="block font-display text-lg group-hover:text-accent transition-colors">
                {directMatch.reference}
                <NavSpinner />
              </span>
            </Link>
          )}

          {suggestions.length > 0 && (
            <>
              <p className="text-xs font-medium uppercase tracking-widest text-muted-foreground mb-2">
                Related questions
              </p>
              <ul className="space-y-1">
                {suggestions.map((s) =>
                  isDestination(s.href) ? (
                    <li key={s.text}>
                      <Link
                        href={s.href}
                        prefetch={true}
                        onClick={() => trackSearchResultClick(query, 'suggestion', s.href)}
                        className="block text-sm text-left py-1.5 hover:text-accent transition-colors"
                      >
                        {s.text}
                        <NavSpinner />
                      </Link>
                    </li>
                  ) : (
                    /* Refinements point back at this page with ?q= — as a
                       link that navigation looks like a no-op (and the
                       ?q= auto-run only fires on first mount). Run the
                       refined search in place instead. */
                    <li key={s.text}>
                      <button
                        type="button"
                        onClick={() => {
                          trackSearchResultClick(query, 'refinement', s.text);
                          answerAbortRef.current?.abort();
                          setAnswerFor(null);
                          setAnswerStreaming(false);
                          setQuery(s.text);
                        }}
                        className="block w-full text-sm text-left py-1.5 hover:text-accent transition-colors focus:outline-none focus-visible:text-accent"
                      >
                        {s.text}
                      </button>
                    </li>
                  )
                )}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}

